import { useMemo, useState } from 'react';
import { matchPath, Navigate, Outlet, useLocation } from 'react-router-dom';

import { useMachines } from '../../features/machines/api/useMachines';
import { useAuth } from '../../auth/AuthProvider';
import { permissions } from '../../auth/permissions';
import { organizationSwitcherMemberships } from '../../auth/organization-access';
import { useRecentWork } from './useRecentWork';
import { AceGuidancePanel } from '../../features/product-guidance/components/AceGuidancePanel';
import { ProductTourOnboardingOffer } from '../../features/product-tour/components/ProductTourOnboardingOffer';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { MobileNavigation } from './MobileNavigation';

export function AppShell() {
  const location = useLocation();
  const auth = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const routeMachineId = useMemo(() => {
    // /machines/new is a setup route, not a machine workspace. Treating "new" as
    // a machine id was what caused the first-run shell to show "Loading machine"
    // and machine-only navigation before any machine existed.
    if (location.pathname === '/machines/new') return null;
    const match = matchPath('/machines/:machineId/*', location.pathname) ?? matchPath('/machines/:machineId', location.pathname);
    return match?.params.machineId ?? null;
  }, [location.pathname]);

  const machines = useMachines();
  const recentWork = useRecentWork(auth.activeCustomerId, auth.profile?.user.user_id ?? null, machines.data?.machines);
  const canViewOrganization = auth.can(permissions.organizationRead);
  const canViewUsers = auth.can(permissions.usersRead);
  const canSwitchOrganization = organizationSwitcherMemberships(auth.profile?.memberships ?? []).length > 1;
  const shouldCheckArchivedDirectory =
    location.pathname === '/machines'
    && !machines.isPending
    && !machines.isError
    && (machines.data?.machines.length ?? 0) === 0;
  const archivedDirectory = useMachines('', 'ARCHIVED', shouldCheckArchivedDirectory);
  // The machine directory is also the first-run onboarding route. Keep the
  // application chrome out of the way until we know the organization already
  // has machines. This intentionally treats an unresolved/disabled machine
  // query as focused setup rather than rendering a sidebar next to onboarding.
  // Once a non-empty machine list resolves, the normal application shell
  // returns immediately.
  const machineFirstRunFocus =
    location.pathname === '/machines' &&
    !machines.isError &&
    (machines.isPending || (
      (machines.data?.machines.length ?? 0) === 0
      && (archivedDirectory.isPending || (!archivedDirectory.isError && (archivedDirectory.data?.machines.length ?? 0) === 0))
    ));
  // Preserve the normal navigation when choosing a different organization.
  // The post-login handoff remains focused to avoid a one-frame navigation
  // flash while /start resolves the user's machine state.
  const organizationSelectionRoute = location.pathname === '/organizations' || location.pathname === '/customers';
  const focusedShell =
    location.pathname === '/start' ||
    machineFirstRunFocus;
  const routeMachine = machines.data?.machines.find((machine) => machine.id === routeMachineId) ?? null;
  const resolvingMachineRoute = Boolean(routeMachineId && (machines.isPending || (machines.isFetching && !routeMachine)));

  if (routeMachineId && !resolvingMachineRoute && !machines.isError && !routeMachine) {
    return <Navigate to="/machines" replace />;
  }

  const shellMachineId = routeMachine?.id ?? (machines.isError ? routeMachineId : null);
  const machineName = routeMachine?.name ?? null;
  // Ace begins after organization selection, when Route42 has the selected
  // tenant context for permission-aware guidance.
  const showAceGuidance = !organizationSelectionRoute;

  return (
    <div className={`r42-app-shell${focusedShell ? ' r42-app-shell--focus' : ''}`}>
      <TopBar machineId={shellMachineId} onMenu={() => setMobileNavOpen(true)} showNavigation={!focusedShell} showOrganizationSwitch={!organizationSelectionRoute && location.pathname !== '/start'} />
      {!focusedShell ? <Sidebar machineId={shellMachineId} machineName={machineName} canViewOrganization={canViewOrganization} canViewUsers={canViewUsers} canSwitchOrganization={canSwitchOrganization} recentWork={recentWork} /> : null}
      <main className="r42-main">
        {resolvingMachineRoute ? <MachineRouteLoading /> : <Outlet />}
      </main>
      {showAceGuidance ? <AceGuidancePanel machineCount={machines.data?.machines.length ?? null} machineId={shellMachineId} machineName={machineName} /> : null}
      <ProductTourOnboardingOffer
        machineCount={machines.data?.machines.length ?? null}
        machinesReady={!machines.isPending && !machines.isError}
      />
      {mobileNavOpen && !focusedShell ? (
        <MobileNavigation
          machineId={shellMachineId}
          machineName={machineName}
          canViewOrganization={canViewOrganization}
          canViewUsers={canViewUsers}
          canSwitchOrganization={canSwitchOrganization}
          recentWork={recentWork}
          onClose={() => setMobileNavOpen(false)}
        />
      ) : null}
    </div>
  );
}

function MachineRouteLoading() {
  return (
    <section className="r42-page r42-machine-route-loading" role="status" aria-live="polite">
      <span className="r42-learning-dot" aria-hidden="true" />
      <div>
        <strong>Opening machine workspace…</strong>
        <span>Route42 is confirming the machine for this workspace.</span>
      </div>
    </section>
  );
}
